import {ArrowDownIcon, ArrowUpIcon, MinusIcon, SparklesIcon} from "lucide-react";
import {useMemo, useState} from "react";
import {
    Area,
    AreaChart,
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import {SaaSMetricDetailSheet} from "./SaaSMetricDetailSheet";
import {
    DEFAULT_AS_OF,
    METRIC_GROUP_LABELS,
    SAAS_SUMMARY_DATA,
    type MetricFormat,
    type SaaSMetric,
} from "./saasMockData";

// ── Props ────────────────────────────────────────────────────────────────
// Data is a local synthetic fixture, so workspace/scenario props are unused
// for now. Kept for TabProps compatibility with the WorkspaceShell registry.
interface SaaSSummaryTabProps {
    workspaceId: string;
    scenarioId: string;
}

// ── Formatters ─────────────────────────────────────────────────────────

function fmtScorecard(v: number | null, format: MetricFormat): string {
    if (v == null) return "—";
    switch (format) {
        case "percent":
            return `${v.toFixed(1)}%`;
        case "ratio":
            return `${v.toFixed(1)}x`;
        case "months":
            return `${v.toFixed(1)} mo`;
        case "count":
            return `${v}`;
        case "currency":
        default: {
            const abs = Math.abs(v);
            const sign = v < 0 ? "-" : "";
            if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
            if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
            return `${sign}$${abs.toFixed(0)}`;
        }
    }
}

// ── Small presentational pieces ──────────────────────────────────────────

function Delta({deltaPct, inverse}: {deltaPct: number; inverse: boolean}) {
    const tone =
        deltaPct === 0 ? "neutral" : (inverse ? deltaPct < 0 : deltaPct > 0) ? "good" : "bad";
    const color =
        tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-red-600" : "text-slate-500";
    const Icon = deltaPct > 0 ? ArrowUpIcon : deltaPct < 0 ? ArrowDownIcon : MinusIcon;
    return (
        <span className={`inline-flex items-center gap-0.5 font-semibold ${color}`}>
            <Icon className="size-3" />
            {Math.abs(deltaPct).toFixed(1)}%
        </span>
    );
}

function Panel({
    title,
    subtitle,
    legend,
    children,
}: {
    title: string;
    subtitle?: string;
    legend?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <section className="min-w-0 rounded-[10px] border border-slate-200 bg-white shadow-sm">
            <div className="flex items-start justify-between gap-3 px-4 pt-4">
                <div>
                    <h3 className="text-sm font-semibold tracking-tight text-slate-900">{title}</h3>
                    {subtitle && <p className="mt-0.5 text-[11px] text-slate-500">{subtitle}</p>}
                </div>
                {legend && <div className="flex flex-wrap justify-end gap-3 text-[10px] text-slate-500">{legend}</div>}
            </div>
            {children}
        </section>
    );
}

function LegendDot({color, label}: {color: string; label: string}) {
    return (
        <span className="flex items-center gap-1.5">
            <i className="size-2 rounded-[2px]" style={{background: color}} />
            {label}
        </span>
    );
}

// ── Main tab ───────────────────────────────────────────────────────────

export function SaaSSummaryTab(_props: SaaSSummaryTabProps) {
    const data = SAAS_SUMMARY_DATA;
    const [asOf, setAsOf] = useState<string>(DEFAULT_AS_OF);
    const [selectedMetric, setSelectedMetric] = useState<SaaSMetric | null>(null);

    const snapshot = data.months[asOf] ?? data.months[DEFAULT_AS_OF];
    const headlineMetrics = useMemo(() => snapshot.metrics.filter((m) => m.headline), [snapshot]);
    const operatingMetrics = useMemo(() => snapshot.metrics.filter((m) => !m.headline), [snapshot]);

    const bridge = snapshot.arrBridge;
    const bridgeBars = useMemo(() => {
        const max = Math.max(bridge.begin, bridge.end);
        const step = (amount: number) => 12 + (amount / 40) * 34; // % height for movements
        const total = (amount: number) => 45 + (amount / max) * 37;
        return [
            {name: "Opening", amount: bridge.begin, type: "total" as const, height: total(bridge.begin), prefix: ""},
            {name: "New", amount: bridge.new, type: "up" as const, height: step(bridge.new), prefix: "+"},
            {name: "Expansion", amount: bridge.expansion, type: "up" as const, height: step(bridge.expansion), prefix: "+"},
            {name: "Contraction", amount: bridge.contraction, type: "down" as const, height: step(bridge.contraction), prefix: "−"},
            {name: "Churn", amount: bridge.churn, type: "down" as const, height: step(bridge.churn), prefix: "−"},
            {name: "Closing", amount: bridge.end, type: "total" as const, height: total(bridge.end), prefix: ""},
        ];
    }, [bridge]);

    return (
        <div className="flex flex-col gap-5">
            {/* Heading */}
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                <div>
                    <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-[#209DB3]">SaaS Summary</p>
                    <h2 className="mt-1 text-[22px] font-bold tracking-tight text-slate-900">
                        Recurring revenue performance
                    </h2>
                    <p className="mt-1 text-[13px] text-slate-500">{snapshot.periodSubtitle}</p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2.5">
                    <span className="rounded-full bg-[#5bf6c8]/20 px-2.5 py-1.5 text-[11px] font-bold text-teal-700">
                        {data.source}
                    </span>
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                        As of
                        <select
                            value={asOf}
                            onChange={(e) => setAsOf(e.target.value)}
                            className="h-8 rounded-md border border-[#5bf6c8] bg-white px-2 pr-7 text-xs font-bold text-[#1a8c9e] outline-none focus:ring-2 focus:ring-[#209DB3]/20"
                            aria-label="Select reporting month"
                        >
                            {data.availableMonths.map((m) => (
                                <option key={m.value} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            {/* Executive Summary */}
            <section className="overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5">
                    <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-900">
                        <SparklesIcon className="size-4 text-[#209DB3]" />
                        Executive Summary
                        <span className="text-slate-400">({snapshot.label})</span>
                        <span className="rounded-full border border-emerald-300 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">
                            Fresh
                        </span>
                    </div>
                </div>
                <div className="grid gap-x-8 gap-y-2.5 px-5 py-4 md:grid-cols-2">
                    {snapshot.executiveSummary.map((point) => (
                        <p key={point.label} className="relative pl-3.5 text-xs leading-relaxed text-slate-600">
                            <span className="absolute left-0 top-[0.55em] size-1.5 rounded-full bg-[#209DB3]" />
                            <strong className="font-semibold text-slate-900">{point.label}:</strong> {point.text}
                        </p>
                    ))}
                </div>
            </section>

            {/* Headline KPIs */}
            <section>
                <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#209DB3]">Headline KPIs</h3>
                    <span className="text-[11px] text-slate-400">The indicators most relevant to the board</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    {headlineMetrics.map((m) => (
                        <button
                            key={m.key}
                            type="button"
                            onClick={() => setSelectedMetric(m)}
                            className="relative min-h-[116px] overflow-hidden rounded-[10px] border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-[#209DB3] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#209DB3]/30"
                        >
                            <span className="absolute inset-y-0 left-0 w-[3px] bg-[#209DB3]" />
                            <div className="truncate text-[11.5px] font-semibold text-slate-500" title={m.label}>
                                {m.label}
                            </div>
                            <div className="mt-2 text-[23px] font-extrabold tracking-tight text-[#1a8c9e] tabular-nums">
                                {m.display}
                            </div>
                            <div className="mt-2 flex items-center justify-between gap-1.5 text-[10.5px] text-slate-400">
                                <Delta deltaPct={m.deltaPct} inverse={m.inverse} />
                                <span className="truncate">{m.basis}</span>
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {/* Charts row 1 */}
            <div className="grid gap-3.5 lg:grid-cols-[1.35fr_1fr]">
                <Panel
                    title="ARR & Revenue Trend"
                    subtitle="Monthly revenue with annualised recurring run-rate"
                    legend={
                        <>
                            <LegendDot color="#209db3" label="Total Revenue" />
                            <LegendDot color="#ff5100" label="ARR" />
                        </>
                    }
                >
                    <div className="h-[266px] px-3 pb-3 pt-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={data.trend} margin={{top: 10, right: 8, left: -8, bottom: 0}}>
                                <CartesianGrid vertical={false} stroke="#e9eef4" />
                                <XAxis dataKey="label" tick={{fontSize: 10, fill: "#94a3b8"}} axisLine={false} tickLine={false} />
                                <YAxis
                                    yAxisId="rev"
                                    tick={{fontSize: 10, fill: "#94a3b8"}}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `$${v}K`}
                                />
                                <YAxis
                                    yAxisId="arr"
                                    orientation="right"
                                    tick={{fontSize: 10, fill: "#94a3b8"}}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `$${v}M`}
                                />
                                <Tooltip
                                    formatter={(value: number, name: string) =>
                                        name === "ARR" ? [`$${value.toFixed(2)}M`, name] : [`$${value}K`, name]
                                    }
                                    contentStyle={{fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0"}}
                                />
                                <Bar yAxisId="rev" dataKey="revenue" name="Total Revenue" fill="#209db3" fillOpacity={0.78} radius={[3, 3, 0, 0]} maxBarSize={26} />
                                <Line yAxisId="arr" type="monotone" dataKey="arr" name="ARR" stroke="#ff5100" strokeWidth={2.5} dot={{r: 2.5}} activeDot={{r: 4}} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel
                    title="Retention Trend"
                    subtitle="Rolling-TTM revenue retention"
                    legend={
                        <>
                            <LegendDot color="#209db3" label="NRR" />
                            <LegendDot color="#6366f1" label="GRR" />
                        </>
                    }
                >
                    <div className="h-[266px] px-3 pb-3 pt-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.trend} margin={{top: 10, right: 8, left: -8, bottom: 0}}>
                                <CartesianGrid vertical={false} stroke="#e9eef4" />
                                <XAxis dataKey="label" tick={{fontSize: 10, fill: "#94a3b8"}} axisLine={false} tickLine={false} />
                                <YAxis
                                    domain={[88, 118]}
                                    tick={{fontSize: 10, fill: "#94a3b8"}}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `${v}%`}
                                />
                                <Tooltip
                                    formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
                                    contentStyle={{fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0"}}
                                />
                                <Line type="monotone" dataKey="nrr" name="NRR" stroke="#209db3" strokeWidth={2.5} dot={false} activeDot={{r: 4}} />
                                <Line type="monotone" dataKey="grr" name="GRR" stroke="#6366f1" strokeWidth={2.5} dot={false} activeDot={{r: 4}} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>
            </div>

            {/* Charts row 2 */}
            <div className="grid gap-3.5 lg:grid-cols-[1.35fr_1fr]">
                <Panel
                    title="Customer Growth"
                    subtitle="Active recurring-revenue customers"
                    legend={<LegendDot color="#10b981" label="Customers" />}
                >
                    <div className="h-[266px] px-3 pb-3 pt-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.trend} margin={{top: 10, right: 8, left: -18, bottom: 0}}>
                                <defs>
                                    <linearGradient id="saasCustomerFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0" stopColor="#10b981" stopOpacity={0.22} />
                                        <stop offset="1" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid vertical={false} stroke="#e9eef4" />
                                <XAxis dataKey="label" tick={{fontSize: 10, fill: "#94a3b8"}} axisLine={false} tickLine={false} />
                                <YAxis tick={{fontSize: 10, fill: "#94a3b8"}} axisLine={false} tickLine={false} />
                                <Tooltip
                                    formatter={(value: number) => [`${value}`, "Customers"]}
                                    contentStyle={{fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0"}}
                                />
                                <Area type="monotone" dataKey="customers" name="Customers" stroke="#10b981" strokeWidth={2.5} fill="url(#saasCustomerFill)" dot={{r: 2.5}} activeDot={{r: 4}} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel title="MRR Bridge" subtitle={`${snapshot.previousLabel} to ${snapshot.label} movement`}>
                    <div className="relative h-[266px] px-4 pb-4 pt-5">
                        {bridge.reconciled && (
                            <span className="absolute right-4 top-2 text-[10px] font-bold text-emerald-600">
                                ✓ Reconciled
                            </span>
                        )}
                        <div className="flex h-full items-end gap-2 border-b border-slate-200 px-1 pb-9 pt-3">
                            {bridgeBars.map((b) => (
                                <div key={b.name} className="relative flex h-full flex-1 items-end justify-center">
                                    <div
                                        className={`relative w-[min(42px,72%)] rounded-t ${
                                            b.type === "total"
                                                ? "bg-[#1a8c9e]"
                                                : b.type === "up"
                                                  ? "bg-emerald-500"
                                                  : "bg-red-600"
                                        }`}
                                        style={{height: `${b.height}%`}}
                                    >
                                        <span className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-slate-600">
                                            {b.prefix}${b.amount}K
                                        </span>
                                    </div>
                                    <span className="absolute top-[calc(100%+8px)] w-full text-center text-[9px] leading-tight text-slate-500">
                                        {b.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Panel>
            </div>

            {/* Operating Metrics (moved below charts) */}
            <Panel title="Operating Metrics" subtitle="Supporting indicators for the selected month — click any row for detail">
                <div className="overflow-x-auto px-4 pb-4 pt-2">
                    <table className="w-full min-w-[720px] text-[12px]">
                        <thead>
                            <tr className="text-slate-500">
                                <th className="px-2.5 py-2 text-left font-bold">Category</th>
                                <th className="px-2.5 py-2 text-left font-bold">Metric</th>
                                <th className="px-2.5 py-2 text-right font-bold">Current</th>
                                <th className="px-2.5 py-2 text-right font-bold">Movement</th>
                                <th className="px-2.5 py-2 text-right font-bold">Basis</th>
                            </tr>
                        </thead>
                        <tbody>
                            {operatingMetrics.map((m) => (
                                <tr
                                    key={m.key}
                                    tabIndex={0}
                                    role="button"
                                    onClick={() => setSelectedMetric(m)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setSelectedMetric(m);
                                        }
                                    }}
                                    className="cursor-pointer border-b border-slate-100 outline-none hover:bg-[#209db3]/5 focus:bg-[#209db3]/5"
                                >
                                    <td className="px-2.5 py-2.5 text-left text-[9px] font-semibold uppercase tracking-wider text-[#209DB3]">
                                        {METRIC_GROUP_LABELS[m.group]}
                                    </td>
                                    <td className="px-2.5 py-2.5 text-left font-medium text-slate-700">{m.label}</td>
                                    <td className="px-2.5 py-2.5 text-right font-semibold tabular-nums text-slate-800">
                                        {m.display}
                                    </td>
                                    <td className="px-2.5 py-2.5 text-right tabular-nums">
                                        <Delta deltaPct={m.deltaPct} inverse={m.inverse} />
                                    </td>
                                    <td className="px-2.5 py-2.5 text-right text-slate-400">{m.basis}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            {/* Monthly Scorecard */}
            <Panel title="Monthly Scorecard" subtitle="Core SaaS indicators across the latest six months">
                <div className="overflow-x-auto px-4 pb-4 pt-2">
                    <table className="w-full min-w-[820px] text-[11px]">
                        <thead>
                            <tr className="text-slate-500">
                                <th className="sticky left-0 z-10 bg-white px-2.5 py-2 text-left font-bold">Metric</th>
                                {data.scorecardMonths.map((mo) => (
                                    <th key={mo} className="px-2.5 py-2 text-right font-bold">
                                        {mo}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.scorecard.map((row) => {
                                const last = row.values.length - 1;
                                return (
                                    <tr key={row.metric} className="border-b border-slate-100">
                                        <td className="sticky left-0 z-10 bg-white px-2.5 py-2 text-left font-bold text-slate-600">
                                            {row.metric}
                                        </td>
                                        {row.values.map((v, i) => (
                                            <td
                                                key={i}
                                                className={`px-2.5 py-2 text-right tabular-nums ${
                                                    i === last ? "font-bold text-emerald-600" : "text-slate-700"
                                                }`}
                                            >
                                                {fmtScorecard(v, row.format)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <p className="pt-1 text-center text-[11px] text-slate-400">
                Synthetic demonstration data · every metric is explainable — click a KPI or row to see how it was
                built.
            </p>

            <SaaSMetricDetailSheet
                metric={selectedMetric}
                monthLabel={snapshot.label}
                onOpenChange={(open) => !open && setSelectedMetric(null)}
            />
        </div>
    );
}
